/**
 * LTTB (Largest-Triangle-Three-Buckets) Downsampling Algorithm
 * Preserves visual accuracy while dramatically reducing point count
 * O(n) complexity - handles millions of points efficiently
 */

export function lttbDownsample(
  timestamps: number[],
  values: (number | null)[],
  threshold: number
): { timestamps: number[]; values: (number | null)[] } {
  const dataLength = timestamps.length;

  if (threshold >= dataLength || threshold < 3) {
    return { timestamps, values };
  }

  const sampledTimestamps: number[] = [];
  const sampledValues: (number | null)[] = [];

  // Always keep first point
  sampledTimestamps.push(timestamps[0]);
  sampledValues.push(values[0]);

  // Bucket size (leave room for first and last points)
  const bucketSize = (dataLength - 2) / (threshold - 2);

  let a = 0; // Previous selected point index

  for (let i = 0; i < threshold - 2; i++) {
    // Calculate bucket boundaries
    const bucketStart = Math.floor((i + 1) * bucketSize) + 1;
    const bucketEnd = Math.min(Math.floor((i + 2) * bucketSize) + 1, dataLength - 1);

    // Calculate average point in next bucket (for triangle area calculation)
    const avgRangeStart = Math.floor((i + 2) * bucketSize) + 1;
    const avgRangeEnd = Math.min(Math.floor((i + 3) * bucketSize) + 1, dataLength);

    let avgX = 0;
    let avgY = 0;
    let avgCount = 0;

    for (let j = avgRangeStart; j < avgRangeEnd; j++) {
      const val = values[j];
      if (val !== null && Number.isFinite(val)) {
        avgX += timestamps[j];
        avgY += val;
        avgCount++;
      }
    }

    if (avgCount > 0) {
      avgX /= avgCount;
      avgY /= avgCount;
    } else {
      avgX = timestamps[avgRangeStart] || timestamps[bucketStart];
      avgY = 0;
    }

    // Find point in current bucket with largest triangle area
    const pointAX = timestamps[a];
    const pointAY = values[a] ?? 0;

    let maxArea = -1;
    let maxAreaIndex = bucketStart;

    for (let j = bucketStart; j < bucketEnd; j++) {
      const val = values[j];
      if (val === null) continue;

      // Triangle area using cross product
      const area = Math.abs(
        (pointAX - avgX) * (val - pointAY) -
        (pointAX - timestamps[j]) * (avgY - pointAY)
      );

      if (area > maxArea) {
        maxArea = area;
        maxAreaIndex = j;
      }
    }

    sampledTimestamps.push(timestamps[maxAreaIndex]);
    sampledValues.push(values[maxAreaIndex]);
    a = maxAreaIndex;
  }

  // Always keep last point
  sampledTimestamps.push(timestamps[dataLength - 1]);
  sampledValues.push(values[dataLength - 1]);

  return { timestamps: sampledTimestamps, values: sampledValues };
}
